import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BookOpen, ArrowLeft, Heart, Copy, ChevronDown, ChevronUp, Share2, Gift, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DevotionalDisplay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const stateDevotional = location.state?.devotional;

  const [devotional, setDevotional] = useState(stateDevotional);
  const [loading, setLoading] = useState(!stateDevotional && !!id);
  const [saved, setSaved] = useState(false);
  const [offered, setOffered] = useState(devotional?.offered_to_collective || false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [offerAnonymous, setOfferAnonymous] = useState(true);
  const [offerName, setOfferName] = useState('');

  // Fetch devotional by ID if not provided in state
  useEffect(() => {
    if (!stateDevotional && id) {
      const fetchDevotional = async () => {
        try {
          const resp = await axios.get(`${API}/devotionals/${id}`);
          setDevotional(resp.data);
          setOffered(resp.data.offered_to_collective || false);
        } catch (err) {
          console.error('Failed to fetch devotional:', err);
          toast.error('Failed to load devotional');
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      };
      fetchDevotional();
    }
  }, [id, stateDevotional, navigate]);

  if (loading) {
    return (
      <div className="page-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div className="cosmic-loader"></div>
      </div>
    );
  }

  if (!devotional) {
    navigate('/dashboard');
    return null;
  }

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/devotionals/save`, {
        devotional_id: devotional.id
      });
      setSaved(true);
      toast.success('Saved to your library');
    } catch (err) {
      toast.error('Failed to save');
      console.error(err);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(devotional.content);
    toast.success('Copied to clipboard');
  };

  const handleOfferSubmit = async () => {
    try {
      await axios.post(`${API}/collective-library/${devotional.id}/offer`, {
        anonymous: offerAnonymous,
        offered_by_name: offerAnonymous ? null : offerName
      });
      setOffered(true);
      setShowOfferModal(false);
      toast.success('Offered to collective library');
    } catch (err) {
      toast.error('Failed to offer');
      console.error(err);
    }
  };

  const getShareText = () => {
    const excerpt = devotional.content.substring(0, 280);
    return `${devotional.topic}\n\n${excerpt}...\n\n✨ Create your own personalized devotional across world traditions at OmniRead.AI`;
  };

  const handleShareTwitter = () => {
    const text = getShareText();
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const handleShareFacebook = () => {
    const url = `${process.env.REACT_APP_FRONTEND_URL}`;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(getShareText())}`;
    window.open(fbUrl, '_blank', 'width=550,height=420');
  };

  const handleCopyForInstagram = () => {
    const text = getShareText();
    navigator.clipboard.writeText(text);
    toast.success('Copied! Paste into Instagram caption');
  };

  const handleWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: devotional.topic,
          text: getShareText(),
          url: `${process.env.REACT_APP_FRONTEND_URL}`
        });
        toast.success('Shared successfully');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      handleCopyForInstagram();
    }
  };

  const isMultiLens = devotional.structure_type === 'multi-lens' && devotional.lens_sections?.length > 0;

  return (
    <div className="page-container">
      <div className="fade-in" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            variant="ghost"
            style={{ padding: '0.5rem', color: '#d4af37' }}
          >
            <ArrowLeft size={24} />
          </Button>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button
              onClick={handleCopy}
              data-testid="copy-button"
              className="btn-outline-gold"
              style={{ padding: '0.6rem 1.2rem' }}
            >
              <Copy size={16} style={{ marginRight: '0.5rem' }} />
              Copy
            </Button>

            <Button
              onClick={() => setShowShareModal(true)}
              data-testid="share-button"
              className="btn-outline-gold"
              style={{ padding: '0.6rem 1.2rem' }}
            >
              <Share2 size={16} style={{ marginRight: '0.5rem' }} />
              Share
            </Button>

            <Button
              onClick={() => setShowOfferModal(true)}
              data-testid="offer-button"
              disabled={offered}
              className="btn-outline-gold"
              style={{ padding: '0.6rem 1.2rem' }}
            >
              <Gift size={16} style={{ marginRight: '0.5rem' }} />
              {offered ? 'Offered' : 'Offer'}
            </Button>

            <Button
              onClick={handleSave}
              data-testid="save-button"
              disabled={saved}
              className="btn-gold"
              style={{ padding: '0.6rem 1.2rem' }}
            >
              <Heart size={16} style={{ marginRight: '0.5rem' }} fill={saved ? '#0a0a0a' : 'none'} />
              {saved ? 'Saved' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Topic */}
        <div className="mystical-card" style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <BookOpen size={40} color="#d4af37" style={{ margin: '0 auto 1rem' }} />
          <h2 style={{ fontSize: '1.8rem', fontStyle: 'italic', margin: 0 }}>
            {devotional.topic}
          </h2>
          {devotional.category && (
            <p style={{ color: '#f4d03f', marginTop: '0.5rem' }}>{devotional.category}</p>
          )}
        </div>

        {/* Multi-Lens Display */}
        {isMultiLens ? (
          <div>
            {devotional.lens_sections.map((section, idx) => (
              <div key={idx} className="mystical-card" style={{ marginBottom: '1.5rem' }}>
                <div
                  onClick={() => toggleSection(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginBottom: expandedSections[idx] !== false ? '1rem' : 0
                  }}
                >
                  <h3 style={{ margin: 0 }}>{section.lens}</h3>
                  {expandedSections[idx] === false ? <ChevronDown size={24} color="#d4af37" /> : <ChevronUp size={24} color="#d4af37" />}
                </div>

                {expandedSections[idx] !== false && (
                  <div className="devotional-content">
                    <ReactMarkdown>{section.content}</ReactMarkdown>
                  </div>
                )}
              </div>
            ))}

            {devotional.anchor_practices?.length > 0 && (
              <div className="anchor-practices">
                <h3 style={{ marginBottom: '1rem' }}>Anchor Practices</h3>
                <ul style={{ paddingLeft: '1.5rem' }}>
                  {devotional.anchor_practices.map((practice, idx) => (
                    <li key={idx} style={{ marginBottom: '0.75rem', color: '#e0e0e0' }}>
                      {practice}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="mystical-card">
            <div className="devotional-content">
              <ReactMarkdown>{devotional.content}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ marginTop: '3rem', textAlign: 'center' }}>
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="new-reading-button"
            className="btn-outline-gold"
            style={{ padding: '1rem 2rem' }}
          >
            Generate Another Reading
          </Button>
        </div>
      </div>

      {/* Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#d4af37', fontSize: '1.5rem' }}>Offer to Collective Library</DialogTitle>
            <DialogDescription style={{ color: '#c9c9c9', marginTop: '0.5rem' }}>
              Place this devotional on the shelf for someone else who might need it.
            </DialogDescription>
          </DialogHeader>

          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={offerAnonymous}
                  onChange={(e) => setOfferAnonymous(e.target.checked)}
                  data-testid="offer-anonymous-checkbox"
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ color: '#e8e8e8', fontSize: '1rem' }}>Offer anonymously</span>
              </label>
            </div>

            {!offerAnonymous && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d4af37' }}>
                  Your name (e.g., "Sarah M.")
                </label>
                <Input
                  value={offerName}
                  onChange={(e) => setOfferName(e.target.value)}
                  data-testid="offer-name-input"
                  placeholder="Sarah M."
                  style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#e8e8e8',
                    padding: '0.75rem'
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setShowOfferModal(false)}
                className="btn-outline-gold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleOfferSubmit}
                data-testid="confirm-offer-button"
                className="btn-gold"
                disabled={!offerAnonymous && !offerName.trim()}
              >
                Offer to Collective
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#d4af37', fontSize: '1.5rem' }}>Share Devotional</DialogTitle>
            <DialogDescription style={{ color: '#c9c9c9', marginTop: '0.5rem' }}>
              Inspire others to create their own devotional
            </DialogDescription>
          </DialogHeader>

          <div style={{ marginTop: '1.5rem' }}>
            <div className="mystical-card" style={{ padding: '1rem', marginBottom: '1.5rem', background: 'rgba(212, 175, 55, 0.05)' }}>
              <p style={{ fontSize: '1rem', color: '#e8e8e8', fontWeight: 600, marginBottom: '0.5rem' }}>
                {devotional.topic}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#c9c9c9', lineHeight: '1.6' }}>
                {devotional.content.substring(0, 280)}...
              </p>
              <p style={{ fontSize: '0.8rem', color: '#d4af37', marginTop: '0.75rem', fontStyle: 'italic' }}>
                ✨ Create your own at OmniRead.AI
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <Button
                onClick={handleShareTwitter}
                data-testid="share-twitter-button"
                className="btn-outline-gold"
                style={{ width: '100%', padding: '0.75rem' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem' }}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Twitter/X
              </Button>

              <Button
                onClick={handleShareFacebook}
                data-testid="share-facebook-button"
                className="btn-outline-gold"
                style={{ width: '100%', padding: '0.75rem' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem' }}>
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Button
                onClick={handleCopyForInstagram}
                data-testid="copy-instagram-button"
                className="btn-outline-gold"
                style={{ width: '100%' }}
              >
                <Copy size={18} style={{ marginRight: '0.5rem' }} />
                Copy for Instagram
              </Button>

              {navigator.share && (
                <Button
                  onClick={handleWebShare}
                  data-testid="web-share-button"
                  className="btn-gold"
                  style={{ width: '100%' }}
                >
                  <Share2 size={18} style={{ marginRight: '0.5rem' }} />
                  Share...
                </Button>
              )}
            </div>

            <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '1rem', textAlign: 'center', lineHeight: '1.4' }}>
              Share a preview to inspire others to create their own personalized devotional
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}